import type { Address } from "viem";

import { publicEnvironment } from "./public-env";

const envAddress = (value: string | undefined): Address | undefined => (value && /^0x[a-fA-F0-9]{40}$/.test(value) ? (value as Address) : undefined);

export const addresses = {
  brokers: envAddress(publicEnvironment("VITE_BROKER_NFT_ADDRESS")),
  land: envAddress(publicEnvironment("VITE_LAND_TOKEN_ADDRESS")),
  activation: envAddress(publicEnvironment("VITE_ACTIVATION_MANAGER_ADDRESS")),
  escrow: envAddress(publicEnvironment("VITE_TOKEN_ESCROW_ADDRESS")),
  stockBooster: envAddress(publicEnvironment("VITE_STOCK_BOOSTER_ADDRESS")),
  overtimeBooster: envAddress(publicEnvironment("VITE_OVERTIME_BOOSTER_ADDRESS")),
  loanVault: envAddress(publicEnvironment("VITE_LOAN_VAULT_ADDRESS")),
  amm: envAddress(publicEnvironment("VITE_BROKER_AMM_ADDRESS")),
};

export const protocolConfigured = Object.values(addresses).every(Boolean);

export const brokerAbi = [
  {
    type: "function",
    name: "ownerOf",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ type: "address" }],
  },
  {
    type: "function",
    name: "totalSupply",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "maxSupply",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "owner", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "tokenOfOwnerByIndex",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "index", type: "uint256" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "accountOf",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ type: "address" }],
  },
  {
    type: "function",
    name: "tokenWallet",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ type: "address" }],
  },
  {
    type: "function",
    name: "createAccount",
    stateMutability: "nonpayable",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ type: "address" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "tokenId", type: "uint256" },
    ],
    outputs: [],
  },
] as const;

export const erc20Abi = [
  {
    type: "function",
    name: "allowance",
    stateMutability: "view",
    inputs: [
      { name: "owner", type: "address" },
      { name: "spender", type: "address" },
    ],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "name",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "string" }],
  },
  {
    type: "function",
    name: "decimals",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint8" }],
  },
  {
    type: "function",
    name: "symbol",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "string" }],
  },
  {
    type: "function",
    name: "balanceOf",
    stateMutability: "view",
    inputs: [{ name: "account", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "approve",
    stateMutability: "nonpayable",
    inputs: [
      { name: "spender", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
] as const;

export const activationAbi = [
  {
    type: "function",
    name: "totalActiveWeight",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "activeCount",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "burnBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "protocolBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "redirectBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "activeWeight",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "activationOf",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [
      { name: "activated", type: "bool" },
      { name: "tier", type: "uint8" },
    ],
  },
  {
    type: "function",
    name: "tiers",
    stateMutability: "view",
    inputs: [{ name: "tier", type: "uint256" }],
    outputs: [
      { name: "fee", type: "uint256" },
      { name: "multiplierBps", type: "uint16" },
    ],
  },
  {
    type: "function",
    name: "quoteActivation",
    stateMutability: "view",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "tier", type: "uint8" },
    ],
    outputs: [{ name: "feeDue", type: "uint256" }],
  },
  {
    type: "function",
    name: "quoteActivationBatch",
    stateMutability: "view",
    inputs: [
      { name: "tokenIds", type: "uint256[]" },
      { name: "tiersWanted", type: "uint8[]" },
    ],
    outputs: [{ name: "totalFee", type: "uint256" }],
  },
  {
    type: "function",
    name: "activate",
    stateMutability: "nonpayable",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "tier", type: "uint8" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "activateBatch",
    stateMutability: "nonpayable",
    inputs: [
      { name: "tokenIds", type: "uint256[]" },
      { name: "tiersWanted", type: "uint8[]" },
    ],
    outputs: [],
  },
] as const;

export const escrowAbi = [
  {
    type: "function",
    name: "bucketBalance",
    stateMutability: "view",
    inputs: [{ name: "bucketType", type: "uint8" }],
    outputs: [
      { name: "used", type: "uint256" },
      { name: "cap", type: "uint256" },
      { name: "available", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "checkInvariant",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "bool" }],
  },
] as const;

export const boosterAbi = [
  {
    type: "function",
    name: "pendingEth",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "minDropWei",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "currentRound",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "roundActive",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "bool" }],
  },
  {
    type: "function",
    name: "roundCursor",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "roundRecipientCount",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "getStockTokens",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "address[3]" }],
  },
  {
    type: "function",
    name: "startDropPublic",
    stateMutability: "nonpayable",
    inputs: [{ name: "deadline", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "continueDrop",
    stateMutability: "nonpayable",
    inputs: [{ name: "maxRecipients", type: "uint256" }],
    outputs: [],
  },
] as const;

export const overtimeBoosterAbi = [
  ...boosterAbi.filter((entry) => !(entry.type === "function" && entry.name === "getStockTokens")),
  {
    type: "function",
    name: "getStockTokens",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "address[]" }],
  },
] as const;

export const ammAbi = [
  {
    type: "function",
    name: "tokensPerNFT",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "randomFeeBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "specificFeeBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "inventoryCount",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "virtualInventoryRemaining",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "physicalInventoryCount",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "quoteRandomBuy",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "tokenCost", type: "uint256" },
      { name: "ethFee", type: "uint256" },
      { name: "inventorySize", type: "uint256" },
      { name: "nextTokenId", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "quoteSpecificBuy",
    stateMutability: "view",
    inputs: [{ name: "tokenId", type: "uint256" }],
    outputs: [
      { name: "tokenCost", type: "uint256" },
      { name: "ethFee", type: "uint256" },
      { name: "available", type: "bool" },
    ],
  },
  {
    type: "function",
    name: "quoteSellNFT",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "tokenPayout", type: "uint256" },
      { name: "ethFee", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "feeOracleStatus",
    stateMutability: "view",
    inputs: [],
    outputs: [
      { name: "wired", type: "bool" },
      { name: "live", type: "bool" },
      { name: "notionalWei", type: "uint256" },
      { name: "swapFeeWei", type: "uint256" },
      { name: "snipeFeeWei", type: "uint256" },
      { name: "window", type: "uint32" },
      { name: "readyAt", type: "uint256" },
    ],
  },
  {
    type: "function",
    name: "buyRandomNFT",
    stateMutability: "payable",
    inputs: [{ name: "maxCost", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "buySpecificNFT",
    stateMutability: "payable",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "maxCost", type: "uint256" },
    ],
    outputs: [],
  },
  {
    type: "function",
    name: "sellNFT",
    stateMutability: "payable",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "minPayout", type: "uint256" },
    ],
    outputs: [],
  },
] as const;

export const loanVaultAbi = [
  {
    type: "function",
    name: "tokensPerNFT",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "borrowAPYBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "minDuration",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint64" }],
  },
  {
    type: "function",
    name: "maxDuration",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint64" }],
  },
  {
    type: "function",
    name: "gracePeriod",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint64" }],
  },
  {
    type: "function",
    name: "liquidationIncentiveBps",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint16" }],
  },
  {
    type: "function",
    name: "nextLoanId",
    stateMutability: "view",
    inputs: [],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "activeLoans",
    stateMutability: "view",
    inputs: [{ name: "borrower", type: "address" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "quoteBorrowFee",
    stateMutability: "view",
    inputs: [{ name: "duration", type: "uint256" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "quoteLateFee",
    stateMutability: "view",
    inputs: [{ name: "loanId", type: "uint256" }],
    outputs: [{ type: "uint256" }],
  },
  {
    type: "function",
    name: "getLoanDetails",
    stateMutability: "view",
    inputs: [{ name: "loanId", type: "uint256" }],
    outputs: [
      {
        name: "loan",
        type: "tuple",
        components: [
          { name: "borrower", type: "address" },
          { name: "tokenId", type: "uint256" },
          { name: "principal", type: "uint256" },
          { name: "interestRateBps", type: "uint16" },
          { name: "startTimestamp", type: "uint64" },
          { name: "maturityTimestamp", type: "uint64" },
          { name: "graceDeadline", type: "uint64" },
          { name: "status", type: "uint8" },
        ],
      },
    ],
  },
  {
    type: "function",
    name: "borrow",
    stateMutability: "payable",
    inputs: [
      { name: "tokenId", type: "uint256" },
      { name: "duration", type: "uint256" },
    ],
    outputs: [{ name: "loanId", type: "uint256" }],
  },
  {
    type: "function",
    name: "repay",
    stateMutability: "payable",
    inputs: [{ name: "loanId", type: "uint256" }],
    outputs: [],
  },
  {
    type: "function",
    name: "liquidate",
    stateMutability: "nonpayable",
    inputs: [{ name: "loanId", type: "uint256" }],
    outputs: [],
  },
] as const;
